import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import ImageCarousel from './ImageCarousel';

const Hero = () => {
  const { t } = useLanguage();
  const featureItems = t.features?.items || [];
  const highlights = featureItems
    .slice(0, 5)
    .filter((item) => item?.label);
  const heroImageSizes = [256, 320, 384];
  const heroImageSizesAttr = '(min-width: 1024px) 320px, (min-width: 640px) 288px, 256px';
  const publicBase = process.env.PUBLIC_URL || '';
  const buildHeroImage = (baseName, alt) => {
    const basePath = `${publicBase}/images/hero/${baseName}`;
    const buildSrcSet = (ext) => heroImageSizes
      .map((size) => `${basePath}-${size}w.${ext} ${size}w`)
      .join(', ');

    return {
      src: `${basePath}-320w.jpg`,
      srcSet: buildSrcSet('jpg'),
      sources: {
        avif: buildSrcSet('avif'),
        webp: buildSrcSet('webp')
      },
      sizes: heroImageSizesAttr,
      width: 320,
      height: 440,
      alt
    };
  };

  const heroImages = [
    buildHeroImage('IMG_8623', 'Loren Aura brush set hero image'),
    buildHeroImage('IMG_8354', 'Loren Aura brush set detail'),
    buildHeroImage('brush-rose', 'Rose brush set variant'),
    buildHeroImage('brush-purple', 'Purple brush set variant'),
    buildHeroImage('brush-green', 'Green brush set variant')
  ];

  const scrollToOrderForm = () => {
    const orderForm = document.getElementById('order-form');
    if (orderForm) {
      orderForm.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative w-full overflow-hidden bg-[#ffe7f1]" aria-labelledby="hero-title">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 right-0 h-80 w-80 rounded-full bg-[#ffd6e6] blur-3xl opacity-80" />
        <div className="absolute left-[-4rem] top-12 h-64 w-64 rounded-full bg-[#fff1f7]/80 blur-2xl" />
        <div className="absolute bottom-32 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-[#ffeaf4] blur-2xl opacity-80" />
      </div>

      <div className="relative mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 pt-8 pb-16 lg:pt-10 lg:pb-20">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[400px_1fr] lg:gap-12">
          <div
            className="order-1 flex justify-center lg:justify-end motion-safe:animate-fade-in-up"
            style={{ animationDelay: '0.05s' }}
          >
            <div className="relative">
              <div className="rounded-2xl border-[3px] border-[#f2a1c4] bg-white p-3 shadow-[0_25px_45px_-25px_rgba(226,76,139,0.45)]">
                <ImageCarousel images={heroImages} containerClassName="w-64 h-[360px] sm:w-72 sm:h-[400px] lg:w-80 lg:h-[440px]" />
              </div>
              <div className="pointer-events-none absolute -bottom-8 left-1/2 h-12 w-44 -translate-x-1/2 rounded-full bg-[#f6c7db] blur-2xl" />
            </div>
          </div>

          <div
            className="order-2 flex flex-col items-center text-center lg:items-start lg:text-left motion-safe:animate-fade-in-up"
            style={{ animationDelay: '0.15s' }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#b24a7a] sm:text-sm">
              {t.hero.tagline}
            </p>
            <h1
              id="hero-title"
              className="mt-4 max-w-xl text-3xl font-extrabold leading-tight text-[#E24C8B] sm:text-4xl lg:text-5xl"
            >
              {t.hero.title}
            </h1>
            <p className="mt-2 max-w-xl text-lg font-semibold text-[#2d1c2b] sm:text-xl">
              {t.hero.subtitle}
            </p>
            <p className="mt-2 max-w-xl text-base text-[#4b3a2a] sm:text-lg">
              {t.hero.description}
            </p>

            <div className="mt-6 w-full max-w-xl">
              <ul className="space-y-2">
                {highlights.map((item, index) => (
                  <li
                    key={`${item.icon}-${index}`}
                    className="flex items-center gap-3 border-b border-[#f2bfd4] pb-2 last:border-b-0 last:pb-0"
                  >
                    <span className="text-xl" aria-hidden="true">
                      {item.icon || '*'}
                    </span>
                    <span className="text-base font-medium text-[#2d1c2b]">
                      {item.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <button
                onClick={scrollToOrderForm}
                className="group inline-flex items-center gap-3 rounded-full border-2 border-[#f2a1c4] bg-[#E24C8B] px-8 py-3 text-base font-bold text-white shadow-[0_12px_25px_-15px_rgba(226,76,139,0.85)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#C83E78] hover:shadow-[0_18px_30px_-18px_rgba(226,76,139,0.9)] active:translate-y-[1px] sm:text-lg"
                aria-label={t.hero.buyNow}
              >
                <span className="material-symbols-outlined transition-transform duration-200 group-hover:translate-x-0.5" style={{ fontSize: '22px' }}>
                  shopping_cart
                </span>
                {t.hero.buyNow}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-0 left-0 w-full" aria-hidden="true">
        <svg viewBox="0 0 1440 160" className="h-[120px] w-full" preserveAspectRatio="none">
          <path
            d="M0,96L60,90.7C120,85,240,75,360,80C480,85,600,107,720,112C840,117,960,107,1080,101.3C1200,96,1320,96,1380,96L1440,96L1440,160L1380,160C1320,160,1200,160,1080,160C960,160,840,160,720,160C600,160,480,160,360,160C240,160,120,160,60,160L0,160Z"
            fill="#fff1f7"
          />
          <path
            d="M0,128L80,122.7C160,117,320,107,480,112C640,117,800,139,960,144C1120,149,1280,139,1360,133.3L1440,128L1440,160L1360,160C1280,160,1120,160,960,160C800,160,640,160,480,160C320,160,160,160,80,160L0,160Z"
            fill="#f6cfe2"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
